import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  type IPaymentPageAbandonmentProperties,
  type WebViewBridge,
  WebViewBridgeService
} from '@limehome/webview-bridge';
import { cookieVariables } from '../../constants/cookie-variables.constants';
import { CookieAppService } from '../cookie-app/cookie-app.service';

@Injectable({
  providedIn: 'root'
})
export class NativeAppService {
  private readonly bridgeInstance: WebViewBridge;

  constructor(
    private readonly cookieService: CookieAppService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    // Only initialize WebViewBridge in browser environment
    if (isPlatformBrowser(this.platformId)) {
      this.bridgeInstance = WebViewBridgeService.getInstance();
    } else {
      // Use mock implementation for SSR
      this.bridgeInstance = WebViewBridgeService.getMockInstance();
    }
  }

  get appInfo() {
    return this.bridgeInstance.appInfo;
  }

  get securityHeader() {
    return this.bridgeInstance.securityHeader;
  }

  isNativeApp(): boolean {
    const value = this.cookieService.getCookie(cookieVariables.isNativeAppKey);
    return value ? JSON.parse(value) : false;
  }

  setIsNativeApp(value: boolean): void {
    this.cookieService.setCookie(cookieVariables.isNativeAppKey, JSON.stringify(value));
  }

  removeIsNativeApp(): void {
    this.cookieService.removeCookie(cookieVariables.isNativeAppKey);
  }

  initiateOpenMapOnApp(url: string): void {
    this.bridgeInstance.initiateOpenMapApp(url);
  }

  initiateOpenPDPOnApp(url: string): void {
    this.bridgeInstance.initiateOpenPDP(url);
  }

  initiateNativeAppAuth(): void {
    this.bridgeInstance.initiateAuth();
  }

  notifyIntentToOpenBottomSheet(modalId: string): void {
    this.bridgeInstance.notifyIntentToOpenBottomSheet(modalId);
  }

  notifyIntentToCloseBottomSheet(modalId: string): void {
    this.bridgeInstance.notifyIntentToCloseBottomSheet(modalId);
  }

  notifyIntentToShowOverlay(): void {
    this.bridgeInstance.notifyIntentToShowOverlay();
  }

  notifyIntentToHideOverlay(): void {
    this.bridgeInstance.notifyIntentToHideOverlay();
  }

  notifyIntentToLogout(): void {
    this.bridgeInstance.notifyIntentToLogout();
  }

  notifyIntentToOpenFAQ(url: string): void {
    url += `&help_platform=lime_app`;
    this.bridgeInstance.initiateOpenFAQInApp(url);
  }

  notifyIntentToAddToCalendar(payload: Parameters<WebViewBridge['notifyIntentToAddToCalendar']>[0]): void {
    this.bridgeInstance.notifyIntentToAddToCalendar(payload);
  }

  notifyIntentToOpenWhatsapp(url: string): void {
    this.bridgeInstance.notifyIntentToOpenWhatsapp(url);
  }

  notifyIntentToNavigateTo(props: Parameters<WebViewBridge['notifyIntentToNavigateTo']>[0]): void {
    this.bridgeInstance.notifyIntentToNavigateTo(props);
  }

  notifyBookingCompleted(): void {
    this.bridgeInstance.notifyBookingCompleted();
  }

  notifyCheckinCompleted(): void {
    this.bridgeInstance.notifyCheckinCompleted();
  }

  notifyIntentToOpenInAppBrowser(url: string): void {
    this.bridgeInstance.notifyIntentToOpenInAppBrowser(url);
  }

  initiateRequestToken() {
    this.bridgeInstance.initiateRequestToken();
  }

  sendCheckoutPageParams(params: IPaymentPageAbandonmentProperties): void {
    this.bridgeInstance.sendCheckoutPageParams(params);
  }
}
